/* Browser-side upload helpers.
 *
 * The upload endpoint takes a base64 data URL. For photos we resize large ones
 * on a canvas before sending: it caps the longest edge at MAX_EDGE and
 * re-encodes as JPEG to keep request bodies small. PDFs are read through
 * untouched — resizing them on a canvas is meaningless and would corrupt the
 * file — so they go to the server as their raw bytes. */

const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;

/* Read a file straight to a data URL ("data:<mime>;base64,…") without touching
   its bytes. Used for PDFs, which can't (and shouldn't) go through the canvas
   resize path. */
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not decode image"));
    };
    img.src = url;
  });
}

/* Turn an upload into a data URL ready to POST. PDFs are read as-is; images are
   resized to a JPEG data URL ("data:image/jpeg;base64,…"). */
export async function fileToDataUrl(file: File): Promise<string> {
  if (file.type === "application/pdf") return readAsDataUrl(file);
  const img = await loadImage(file);
  const longest = Math.max(img.naturalWidth, img.naturalHeight);
  const scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1;
  const width = Math.round(img.naturalWidth * scale);
  const height = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}
